import { Heading, SearchImage } from '@rabbit/elements/shared-components';
import ROUTE_NAME from '../../../utils/url-constants';
import { DTVendable } from '@rabbit/data/types';
import { useNavigate } from 'react-router-dom';
import { FunnelIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

export interface SearchGalleryProps {
  data?: DTVendable[];
  onChangeStep?: (step_in: number, data: any) => void;
  viewType?: 'grid' | 'list';
}

export function SearchGallery({ data, onChangeStep, viewType = 'grid' }: SearchGalleryProps) {
  const navigate = useNavigate();

  return data ? (
    <div className="w-full">
      <div className="mb-6 flex items-center">
        <div className="w-1 h-6 bg-primary-600 rounded-full mr-2"></div>
        <Heading kind="h3" children="Products" />
        <div className="ml-auto flex items-center">
          <span className="text-sm text-gray-500 mr-2">
            {data.length} {data.length === 1 ? 'product' : 'products'} found
          </span>
        </div>
      </div>

      {data.length > 0 ? (
        viewType === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {data.map((item: any, index: number) => (
              <div
                key={index}
                className="font-nunito transform transition-transform duration-300 hover:translate-y-[-5px]"
              >
                <div className="h-full rounded-lg bg-white shadow-sm hover:shadow-xl transition-all duration-300">
                  <SearchImage
                    imageUrl={item.img[0]}
                    size={onChangeStep ? 'NLIC' : 'md'}
                    subtitle={item.brand}
                    title={item.title}
                    mpn={item.mpn}
                    onClick={() =>
                      onChangeStep
                        ? onChangeStep(3, { docid: item.docid, item })
                        : navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col space-y-4">
            {data.map((item: any, index: number) => (
              <div
                key={index}
                className="font-nunito w-full"
              >
                <div className="flex flex-col sm:flex-row rounded-lg bg-white shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 overflow-hidden">
                  <div className="sm:w-32 md:w-40 lg:w-64 h-32 sm:h-auto">
                    <div
                      style={{ backgroundImage: `url(${item.img[0]})` }}
                      className="w-full h-full bg-contain bg-no-repeat bg-center cursor-pointer hover:opacity-90 transition-opacity duration-300"
                      onClick={() =>
                        onChangeStep
                          ? onChangeStep(3, { docid: item.docid, item })
                          : navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                      }
                    ></div>
                  </div>
                  <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex flex-wrap items-center mb-2">
                        <span className="inline-block px-2 py-1 text-xs font-semibold text-primary-700 bg-primary-50 rounded-full">
                          {item.brand}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 hover:text-primary-700 transition-colors duration-200 mb-2 cursor-pointer line-clamp-2"
                        onClick={() =>
                          onChangeStep
                            ? onChangeStep(3, { docid: item.docid, item })
                            : navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                        }
                      >
                        {item.title}
                      </h3>
                      <p className="text-sm text-primary-700 font-medium flex items-center">
                        <span className="text-xs text-gray-500 mr-1">MPN:</span> {item.mpn}
                      </p>
                    </div>
                    <div className="mt-3 sm:mt-4 flex justify-center sm:justify-end">
                      <button
                        onClick={() =>
                          onChangeStep
                            ? onChangeStep(3, { docid: item.docid, item })
                            : navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                        }
                        className="w-full sm:w-auto text-sm text-white bg-primary-600 hover:bg-primary-700 font-medium px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="w-full rounded-lg bg-gray-50 py-12 text-center text-gray-500 border border-gray-200">
          <div className="flex flex-col items-center">
            <FunnelIcon className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-xl font-medium text-gray-700">No products found matching your criteria.</p>
            <p className="mt-2 text-gray-500 max-w-md mx-auto">
              Try adjusting your filters or search term to find what you're looking for.
            </p>
            <div className="mt-6 flex items-center justify-center">
              <AdjustmentsHorizontalIcon className="h-5 w-5 text-primary-600 mr-2" />
              <span className="text-primary-600 font-medium">Adjust filters</span>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;
}

export default SearchGallery;
